import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { API_URL } from "../../config";

function toE164(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits[0] === "1") return `+${digits}`;
  return `+${digits}`;
}

function formatDisplay(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

interface Props {
  mode: "signin" | "signup";
  redirectTo: string;
}

export default function PhoneAuth({ mode, redirectTo }: Props) {
  const [phoneDisplay, setPhoneDisplay] = useState("");
  const [step, setStep] = useState<"phone" | "code">("phone");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");

  const e164 = toE164(phoneDisplay);
  const phoneValid = /^\+[1-9]\d{9,}$/.test(e164);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneDisplay(formatDisplay(e.target.value));
    setError(null);
  };

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phoneValid) {
      setError("Enter a valid 10-digit US phone number");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/send-sms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: e164, mode }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Failed to send code");
        return;
      }
      setStep("code");
    } catch {
      setError("Connection failed — please try again");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{4}$/.test(code)) {
      setError("Enter the 4-digit code");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-sms`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: e164, code, mode }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? "Verification failed");
        return;
      }
      login(data.jwtToken);
      navigate(next || redirectTo);
    } catch {
      setError("Connection failed — please try again");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%", padding: 10, margin: "10px 0", boxSizing: "border-box",
    fontSize: 16,
  };

  if (step === "code") {
    return (
      <form onSubmit={verifyCode}>
        {error && <p style={{ color: "#ef4444", marginBottom: 16, fontSize: 14 }}>{error}</p>}
        <p style={{ marginBottom: 8, fontSize: 14, color: "#374151" }}>
          Code sent to <strong>{phoneDisplay}</strong>.{" "}
          <button type="button" onClick={() => { setStep("phone"); setCode(""); setError(null); }}
            style={{ background: "none", border: "none", color: "#2563eb", cursor: "pointer", padding: 0, fontSize: 14 }}>
            Change
          </button>
        </p>
        <input
          type="text"
          inputMode="numeric"
          maxLength={4}
          placeholder="4-digit code"
          value={code}
          onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 4)); setError(null); }}
          autoFocus
          style={inputStyle}
        />
        <button type="submit" disabled={loading} style={{ padding: "10px 20px", width: "100%" }}>
          {loading ? "Verifying..." : "Verify Code"}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={sendCode}>
      {error && <p style={{ color: "#ef4444", marginBottom: 16, fontSize: 14 }}>{error}</p>}
      <input
        type="tel"
        placeholder="(555) 555-5555"
        value={phoneDisplay}
        onChange={handlePhoneChange}
        autoFocus
        style={inputStyle}
      />

      {mode === "signup" && (
        <div style={{ margin: "16px 0", padding: 12, backgroundColor: "#f3f4f6", borderRadius: 4 }}>
          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={smsOptIn}
              onChange={(e) => setSmsOptIn(e.target.checked)}
              style={{ marginTop: 2, cursor: "pointer" }}
            />
            <span>
              I consent to receive transactional SMS messages from Lets Go Sauna, including booking confirmations, status updates, and verification codes. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for support.{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", textDecoration: "none" }}>
                Terms
              </a>
              {" | "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", textDecoration: "none" }}>
                Privacy
              </a>
            </span>
          </label>
        </div>
      )}

      <button
        type="submit"
        disabled={loading || !phoneValid || (mode === "signup" && !smsOptIn)}
        style={{ padding: "10px 20px", width: "100%", opacity: (mode === "signup" && !smsOptIn) ? 0.5 : 1 }}
      >
        {loading ? "Sending..." : "Send Code"}
      </button>
    </form>
  );
}
